import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';
import { secondsToHours } from 'date-fns';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { getQuotaUsage } from 'store/recordings/actions';

import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useRecordingPermissions from 'utils/hooks/useRecordingPermissions';
import { isWeb } from 'utils/responsiveness';

import DialogView from 'components/Dialog';

import {
    SCREEN_DETAIL_TIME_HOURS,
    SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_PROTECTED,
    SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_USED,
    SETTINGS_RECORDING_QUOTA_BODY_REACT_NATIVE,
    SETTINGS_RECORDING_QUOTA_NOT_ENTITLED_BODY_REACT_NATIVE,
    SETTINGS_RECORDING_QUOTA_NOT_ENTITLED_TITLE,
    SETTINGS_RECORDING_QUOTA_TITLE,
} from '../languagesConst';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.SETTINGS_RECORDING_QUOTA
    >;
};

const {
    CLOSE_BUTTON,
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_CLOSE_BUTTON },
        SETTINGS: {
            RECORDING_QUOTA: {
                ACCESSIBILITY_SETTINGS_REC_QUOTA_HOUR,
                ACCESSIBILITY_SETTINGS_REC_QUOTA_HOURS,
            },
        },
    },
} = LANGUAGE_KEYS;

function Quota({ navigation }: Props) {
    const { primary } = useSelector(selectLayoutConfig);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [totalQuota, setTotalQuota] = useState<number>(0);
    const [recordedQuota, setRecordedQuota] = useState<number>(0);
    const [protectedQuota, setProtectedQuota] = useState<number>(0);
    const [isFetchingData, setIsFetchingData] = useState<boolean>(true);
    const [visible, setVisible] = useState(true);

    const { userHasRecordingPermissions } = useRecordingPermissions();

    const handleOnClosePress = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else if (isWeb) {
            window.history.back();
        }

        setVisible(false);

        return false;
    }, [navigation]);

    useBackHandler(handleOnClosePress);

    useEffect(() => {
        setIsFetchingData(true);

        dispatch(
            getQuotaUsage({
                onSuccess: (data) => {
                    if (data) {
                        setTotalQuota(parseInt(data?.quotaTotal, 10));
                        setRecordedQuota(
                            parseInt(data?.usageSummary?.quotaRecorded, 10),
                        );
                        setProtectedQuota(
                            parseInt(
                                data?.usageSummary?.protectedRecordings,
                                10,
                            ),
                        );
                    }
                    setIsFetchingData(false);
                },
            }),
        );
    }, []);

    const usedQuotaPercent = useMemo(() => {
        if (recordedQuota && totalQuota) {
            return (recordedQuota * 100) / totalQuota;
        }

        return 0;
    }, [recordedQuota, totalQuota]);

    const protectedQuotaPercent = useMemo(() => {
        if (protectedQuota && recordedQuota) {
            return (protectedQuota * 100) / recordedQuota;
        }

        return 0;
    }, [protectedQuota, recordedQuota]);

    const getQuotaAccessibilityLabel = useCallback(
        (quota: number) => {
            const hours = Math.floor(secondsToHours(quota) || 0);
            const isPlural = hours !== 1;

            const hourKey = isPlural
                ? ACCESSIBILITY_SETTINGS_REC_QUOTA_HOURS
                : ACCESSIBILITY_SETTINGS_REC_QUOTA_HOUR;

            return `${hours} ${t(hourKey)}`;
        },
        [t],
    );

    const progressAccessibilityLabel = useMemo(
        () =>
            `${getQuotaAccessibilityLabel(protectedQuota)} ${
                t(SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_PROTECTED).split(' ')[1]
            } ${getQuotaAccessibilityLabel(recordedQuota)} ${
                t(SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_USED).split(' ')[1]
            } ${getQuotaAccessibilityLabel(totalQuota)}`,
        [
            getQuotaAccessibilityLabel,
            protectedQuota,
            recordedQuota,
            totalQuota,
            t,
        ],
    );

    const renderHoursStatus = useCallback(() => {
        if (isFetchingData) {
            return null;
        }

        return (
            <View style={styles.hoursStatus}>
                <View style={styles.usedHours}>
                    <Text
                        style={[styles.hoursText, { color: primary }]}
                        accessibilityLabel={`${getQuotaAccessibilityLabel(
                            protectedQuota,
                        )} ${
                            t(
                                SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_PROTECTED,
                            ).split(' ')[1]
                        }`}
                    >
                        {secondsToHours(protectedQuota)}
                        {t(SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_PROTECTED)}
                    </Text>

                    <Text
                        style={[styles.hoursText, { color: primary }]}
                        importantForAccessibility="no"
                        accessibilityElementsHidden
                    >
                        {' / '}
                    </Text>

                    <Text
                        style={styles.hoursText}
                        accessibilityLabel={`${getQuotaAccessibilityLabel(
                            recordedQuota,
                        )} ${
                            t(SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_USED).split(
                                ' ',
                            )[1]
                        }`}
                    >
                        {secondsToHours(recordedQuota)}
                        {t(SCREEN_SETTINGS_RECORDING_QUOTA_HOURS_USED)}
                    </Text>
                </View>

                <Text
                    style={styles.hoursText}
                    accessibilityLabel={getQuotaAccessibilityLabel(totalQuota)}
                >
                    {secondsToHours(totalQuota)}
                    {t(SCREEN_DETAIL_TIME_HOURS)}
                </Text>
            </View>
        );
    }, [isFetchingData, primary, protectedQuota, t, recordedQuota, totalQuota]);

    const renderProgressBar = useCallback(() => {
        if (isFetchingData) {
            return <View style={styles.emptyProgressBar} />;
        }

        return (
            <View
                style={styles.progressBar}
                tabIndex={0}
                accessible
                accessibilityLabel={progressAccessibilityLabel}
            >
                <View
                    style={[
                        styles.usedQuota,
                        { width: `${usedQuotaPercent}%` },
                    ]}
                >
                    <View
                        style={[
                            styles.protectedQuota,
                            {
                                width: `${protectedQuotaPercent}%`,
                                backgroundColor: primary,
                            },
                        ]}
                    />
                </View>
            </View>
        );
    }, [isFetchingData, usedQuotaPercent, protectedQuotaPercent, primary]);

    // TODO: Implement logic for selfcare when decided to be added
    /* const handleOnSelfCarePress = useCallback(() => {}, []);
    const renderSelfCareButton = useCallback(() => {
        return (
            <ActionButton
                numberOfLines={1}
                buttonStyle={[styles.button, styles.selfcareButton]}
                buttonTextStyle={styles.buttonText}
                title={SELFCARE_APP_BUTTON}
                fillColor={primary}
                onPress={handleOnSelfCarePress}
            />
        );
    }, [handleOnSelfCarePress]); */

    const renderDescriptionComponent = useCallback(() => {
        return (
            <>
                <Text
                    style={styles.descriptionText}
                    // @ts-ignore
                    tabIndex={0}
                >
                    {userHasRecordingPermissions
                        ? t(SETTINGS_RECORDING_QUOTA_BODY_REACT_NATIVE)
                        : t(
                              SETTINGS_RECORDING_QUOTA_NOT_ENTITLED_BODY_REACT_NATIVE,
                          )}
                </Text>

                {userHasRecordingPermissions && renderHoursStatus()}
                {userHasRecordingPermissions && renderProgressBar()}
            </>
        );
    }, [renderHoursStatus, renderProgressBar, userHasRecordingPermissions]);

    return (
        <DialogView
            style={styles.dialog}
            title={
                userHasRecordingPermissions
                    ? t(SETTINGS_RECORDING_QUOTA_TITLE)
                    : t(SETTINGS_RECORDING_QUOTA_NOT_ENTITLED_TITLE)
            }
            actionButtonTitle={CLOSE_BUTTON}
            cancelButtonAccessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
            actionButtonOnPress={handleOnClosePress}
            actionButtonStyle={styles.button}
            actionButtonTextStyle={styles.actionButtonText}
            cancelButtonOnPress={handleOnClosePress}
            actionButtonBordered
            DescriptionComponent={renderDescriptionComponent}
            visible={visible}
        />
    );
}

export default Quota;
